import { MailIcon, PlusIcon, XIcon } from '@heroicons/react/solid'
import { useMemo, useState } from 'react'
import { Paragraph, Tag } from 'src/components'
import { ItemOf, throwUnsupportedValue } from 'src/utils'
import { useListCognitoUsers, useListConnectUsers } from '../queries'
import { useCreateCognitoUser, useDeleteCognitoUser } from './queries'
import InputField from 'src/components/Shared/UIComponents/InputField'
import CustomButton from 'src/components/Shared/UIComponents/Button'

type Props = ItemOf<typeof useListConnectUsers> & {
  cognito: ItemOf<typeof useListCognitoUsers> | undefined
  organization: string
  username: string
}

export function UsersItem(props: Props) {
  const [email, setEmail] = useState('')
  const createUser = useCreateCognitoUser()
  const deleteUser = useDeleteCognitoUser()

  const shownEmail = useMemo(() => {
    if (props.cognito) {
      const attribute = props.cognito.Attributes.find(
        (item) => item.Name === 'email',
      )
      return attribute ? attribute.Value : '-'
    }

    return '-'
  }, [props.cognito])

  return (
    <>
      <div className="flex flex-row">
        <Tag
          title={props.cognito?.UserStatus ?? '-'}
          label={props.Username.slice(0, 1).toUpperCase()}
          color={getTagColorFromStatus(props.cognito?.UserStatus)}
          className="self-center mr-3"
        />

        <div className="flex flex-col justify-center">
          <div className="leading-6 text-slate-700">{props.Username}</div>
        </div>
      </div>

      <div className="flex flex-row">
        {props.cognito ? (
          <Paragraph className="flex flex-row items-center flex-1">
            <MailIcon className="w-5 h-5 mx-2 text-slate-400/75" />
            {shownEmail}
          </Paragraph>
        ) : (
          <InputField
            label="email"
            value={email}
            setValue={setEmail}
            type="email"
            placeholder="type user email"
            hideLabel
            className="self-center flex-1"
          />
        )}

        {props.cognito ? (
          <CustomButton
            label="Remove"
            loadingLabel="Removing"
            onClick={() =>
              deleteUser.mutate({
                organization: props.organization,
                username: props.username,
                usernamefordelete: props.Username,
              })
            }
            isLoading={deleteUser.isLoading}
            iconPosition="left"
            icon={<XIcon />}
            className="self-center ml-6"
          />
        ) : (
          <CustomButton
            label="Add"
            loadingLabel="Adding"
            onClick={() => {
              createUser.mutate({
                organization: props.organization,
                username: props.username,
                usernameforcreate: props.Username,
                id: props.Id,
                email: email,
                code: props.organization,
              })
            }}
            isLoading={createUser.isLoading}
            iconPosition="left"
            icon={<PlusIcon />}
            className="self-center ml-6"
            disabled={!email}
          />
        )}
      </div>
    </>
  )
}

function getTagColorFromStatus(
  userStatus: Exclude<Props['cognito'], undefined>['UserStatus'] | undefined,
) {
  switch (userStatus) {
    case 'CONFIRMED':
      return 'green'
    case 'FORCE_CHANGE_PASSWORD':
      return 'yellow'
    case undefined:
      return 'red'
    default:
      throwUnsupportedValue(userStatus)
  }
}
